import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
    Activity,
    Moon,
    Heart,
    Brain,
    Calendar,
    ChevronLeft,
    ChevronRight,
    ShieldCheck
} from "lucide-react";
import SectionHeader from "../SectionHeader";
import { useTranslation } from "react-i18next";

// Import translations
import fa from "./locales/AppExperience-fa.json";
import en from "./locales/AppExperience-en.json";
import ar from "./locales/AppExperience-ar.json";
import tr from "./locales/AppExperience-tr.json";

interface StatItem {
    label: string;
    value: string;
}

interface FeatureItem {
    id: string;
    title: string;
    subtitle?: string;
    description: string;
    image: string;
    stats?: StatItem[];
    icon: React.ReactNode;
    color: string;
    gradient: string;
    bgLight: string;
    border: string;
    glow: string;
}

const AppExperience: React.FC = () => {
    const { t, i18n } = useTranslation();

    useEffect(() => {
        const ns = "AppExperience";
        const resources = { fa, en, ar, tr } as const;
        Object.entries(resources).forEach(([lng, data]) => {
            if (!i18n.hasResourceBundle(lng, ns)) {
                i18n.addResourceBundle(lng, ns, data, true, true);
            }
        });
    }, [i18n]);

    const [activeFeature, setActiveFeature] = useState(0);

    const currentLang = i18n.language;
    const isRTL = ["fa", "ar"].includes(currentLang);
    const dir = isRTL ? "rtl" : "ltr";

    // Automatic photo rotation timer (every 4 seconds)
    useEffect(() => {
        const interval = setInterval(() => {
            setActiveFeature((prev) => (prev + 1) % 5);
        }, 4000);
        return () => clearInterval(interval);
    }, []);

    // Optimized custom vector icons for each of the 5 pages
    const featureIcons = [
        <ShieldCheck key="fi0" className="w-5 h-5 sm:w-6 sm:h-6" />,
        <Moon key="fi1" className="w-5 h-5 sm:w-6 sm:h-6" />,
        <Brain key="fi2" className="w-5 h-5 sm:w-6 sm:h-6" />,
        <Heart key="fi3" className="w-5 h-5 sm:w-6 sm:h-6" />,
        <Calendar key="fi4" className="w-5 h-5 sm:w-6 sm:h-6" />,
    ];

    const featureColors = [
        {
            color: "#0284C7", // Health - Sky Blue / Cyan
            gradient: "linear-gradient(135deg, #0284C7 0%, #06B6D4 100%)",
            bgLight: "rgba(2, 132, 199, 0.06)",
            border: "rgba(2, 132, 199, 0.22)",
            glow: "rgba(2, 132, 199, 0.3)",
        },
        {
            color: "#6366F1", // Sleep - Indigo / Violet
            gradient: "linear-gradient(135deg, #4F46E5 0%, #818CF8 100%)",
            bgLight: "rgba(99, 102, 241, 0.06)",
            border: "rgba(99, 102, 241, 0.22)",
            glow: "rgba(99, 102, 241, 0.3)",
        },
        {
            color: "#D97706", // Stress - Warm Amber
            gradient: "linear-gradient(135deg, #D97706 0%, #F59E0B 100%)",
            bgLight: "rgba(217, 119, 6, 0.06)",
            border: "rgba(217, 119, 6, 0.22)",
            glow: "rgba(217, 119, 6, 0.3)",
        },
        {
            color: "#E11D48", // Heart - Crimson / Rose
            gradient: "linear-gradient(135deg, #E11D48 0%, #FB7185 100%)",
            bgLight: "rgba(225, 29, 72, 0.06)",
            border: "rgba(225, 29, 72, 0.22)",
            glow: "rgba(225, 29, 72, 0.3)",
        },
        {
            color: "#059669", // Today - Emerald / Green
            gradient: "linear-gradient(135deg, #059669 0%, #34D399 100%)",
            bgLight: "rgba(5, 150, 105, 0.06)",
            border: "rgba(5, 150, 105, 0.22)",
            glow: "rgba(5, 150, 105, 0.3)",
        },
    ];

    const features: FeatureItem[] = Array.from({ length: 5 }, (_, i) => {
        const feat = t(`features.${i}`, { ns: "AppExperience", returnObjects: true, defaultValue: null }) as any;
        if (feat && feat.title) {
            return {
                ...feat,
                icon: featureIcons[i],
                ...featureColors[i],
            };
        }
        return null;
    }).filter(Boolean) as FeatureItem[];

    if (!features || features.length === 0) {
        return null;
    }

    const currentFeature = features[activeFeature] || features[0];

    const nextFeature = () => {
        setActiveFeature((prev) => (prev + 1) % features.length);
    };

    const prevFeature = () => {
        setActiveFeature((prev) => (prev - 1 + features.length) % features.length);
    };

    // High-end Desktop Phone Mockup View
    const desktopPhoneScreenshot = (
        <div className="relative w-full max-w-[310px] mx-auto group">
            <div className="relative rounded-[44px] p-2.5 bg-slate-900/90 border border-slate-700/50 shadow-[0_25px_60px_-15px_rgba(15,23,42,0.35)] transition-all duration-300 group-hover:shadow-[0_30px_70px_-12px_rgba(0,0,0,0.4)]">
                {/* Frame bezel & Screen Container */}
                <div className="relative rounded-[36px] overflow-hidden aspect-[9/19] bg-slate-950">
                    {/* Speaker Notch / Dynamic Island */}
                    <div className="absolute top-2.5 inset-x-0 flex justify-center z-30 pointer-events-none">
                        <div className="w-20 h-3.5 bg-black rounded-full flex items-center justify-end px-2 border border-white/10">
                            <div className="w-2 h-2 rounded-full bg-blue-500/80"></div>
                        </div>
                    </div>

                    {/* Image Display */}
                    <div className="h-full w-full relative overflow-hidden z-10">
                        <AnimatePresence mode="wait">
                            <motion.div
                                key={activeFeature}
                                initial={{ opacity: 0, scale: 0.95 }}
                                animate={{ opacity: 1, scale: 1 }}
                                exit={{ opacity: 0, scale: 1.03 }}
                                transition={{ duration: 0.35, ease: [0.16, 1, 0.3, 1] }}
                                className="absolute inset-0 w-full h-full"
                            >
                                <img
                                    src={currentFeature.image}
                                    alt={currentFeature.title}
                                    className="absolute inset-0 w-full h-full object-cover object-center select-none"
                                />
                                <div className="absolute inset-0 bg-gradient-to-t from-slate-950/60 via-transparent to-slate-950/20 pointer-events-none"></div>
                            </motion.div>
                        </AnimatePresence>
                    </div>

                    {/* Bottom Indicator Dots */}
                    <div className="absolute bottom-4 inset-x-0 flex justify-center items-center gap-1.5 z-20 pointer-events-none">
                        {features.map((_, i) => (
                            <div
                                key={i}
                                className="h-1.5 rounded-full transition-all duration-300"
                                style={{
                                    width: i === activeFeature ? "22px" : "6px",
                                    backgroundColor: i === activeFeature ? "white" : "rgba(255,255,255,0.4)",
                                }}
                            />
                        ))}
                    </div>
                </div>
            </div>

            {/* Reflection shadow underneath */}
            <div className="absolute -bottom-6 left-1/2 -translate-x-1/2 w-4/5 h-3 bg-slate-900/15 rounded-full blur-lg pointer-events-none"></div>

            {/* Ambient Back Glow synced with feature color */}
            <motion.div
                className="absolute -z-10 w-full h-full rounded-full blur-3xl opacity-30 pointer-events-none"
                style={{
                    background: `radial-gradient(circle at center, ${currentFeature.color}, transparent 70%)`,
                    top: "50%",
                    left: "50%",
                    transform: "translate(-50%, -50%)",
                }}
                animate={{ scale: [1, 1.12, 1], opacity: [0.25, 0.4, 0.25] }}
                transition={{ duration: 3.5, repeat: Infinity, repeatType: "reverse" }}
            />
        </div>
    );

    return (
        <section className="relative overflow-hidden py-10 sm:py-16 bg-slate-50/50" id="app-experience" dir={dir}>
            <div className="container mx-auto px-4 sm:px-6 lg:px-8">
                <SectionHeader
                    regularText={t("sectionHeader.regularText", { ns: "AppExperience" })}
                    highlightedText={t("sectionHeader.highlightedText", { ns: "AppExperience" })}
                    descriptionText={t("sectionHeader.descriptionText", { ns: "AppExperience" })}
                    className="pb-8 sm:pb-12"
                />

                {/* ══════════════════════════════════════════════════════════════
                    MOBILE VIEW (< lg screens)
                    ══════════════════════════════════════════════════════════════ */}
                <div className="block lg:hidden max-w-md mx-auto">
                    {/* Horizontal Compact Feature Tabs */}
                    <div className="flex overflow-x-auto no-scrollbar gap-1.5 p-1.5 bg-slate-200/60 backdrop-blur-md rounded-2xl border border-slate-300/50 mb-6 shadow-inner">
                        {features.map((feature, index) => {
                            const isActive = activeFeature === index;
                            return (
                                <button
                                    key={index}
                                    onClick={() => setActiveFeature(index)}
                                    className={`relative flex-1 min-w-[72px] flex flex-col items-center justify-center py-2.5 px-2 rounded-xl transition-all duration-200 text-center ${
                                        isActive ? "text-white font-bold shadow-sm" : "text-slate-600 hover:text-slate-900 font-medium"
                                    }`}
                                >
                                    {isActive && (
                                        <motion.div
                                            layoutId="active-mobile-pill-v2"
                                            className="absolute inset-0 rounded-xl shadow-md"
                                            style={{ background: feature.gradient }}
                                            transition={{ type: "spring", stiffness: 450, damping: 35 }}
                                        />
                                    )}
                                    <span className="relative z-10 mb-1 flex items-center justify-center">
                                        {React.cloneElement(feature.icon as React.ReactElement<any>, {
                                            className: `w-4 h-4 ${isActive ? "text-white" : "text-slate-600"}`,
                                        })}
                                    </span>
                                    <span className="relative z-10 text-[11px] leading-tight truncate w-full">
                                        {feature.title}
                                    </span>
                                </button>
                            );
                        })}
                    </div>

                    {/* Interactive Mobile Device Frame Showcase */}
                    <div className="relative mb-6">
                        <div className="relative w-full max-w-[270px] mx-auto">
                            <div className="relative rounded-[38px] overflow-hidden border-[3px] border-slate-800 aspect-[9/18.5] bg-slate-950 shadow-xl">
                                <div className="absolute top-2.5 inset-x-0 h-1 flex justify-center z-30">
                                    <div className="w-16 h-1 bg-white/30 rounded-full"></div>
                                </div>

                                <div className="h-full w-full relative overflow-hidden z-10">
                                    <AnimatePresence mode="wait">
                                        <motion.div
                                            key={activeFeature}
                                            initial={{ opacity: 0, x: isRTL ? -25 : 25 }}
                                            animate={{ opacity: 1, x: 0 }}
                                            exit={{ opacity: 0, x: isRTL ? 25 : -25 }}
                                            transition={{ duration: 0.28, ease: "easeOut" }}
                                            drag="x"
                                            dragConstraints={{ left: 0, right: 0 }}
                                            dragElastic={0.2}
                                            onDragEnd={(_, info) => {
                                                const swipeThreshold = 40;
                                                if (info.offset.x > swipeThreshold) {
                                                    if (isRTL) nextFeature();
                                                    else prevFeature();
                                                } else if (info.offset.x < -swipeThreshold) {
                                                    if (isRTL) prevFeature();
                                                    else nextFeature();
                                                }
                                            }}
                                            className="absolute inset-0 w-full h-full cursor-grab active:cursor-grabbing touch-pan-y"
                                        >
                                            <img
                                                src={currentFeature.image}
                                                alt={currentFeature.title}
                                                className="absolute inset-0 w-full h-full object-cover object-center select-none pointer-events-none"
                                            />
                                            <div className="absolute inset-0 bg-gradient-to-t from-slate-950/60 via-transparent to-slate-950/10 pointer-events-none"></div>
                                        </motion.div>
                                    </AnimatePresence>
                                </div>

                                {/* Floating Touch Nav Controls */}
                                <button
                                    onClick={isRTL ? nextFeature : prevFeature}
                                    aria-label="Previous"
                                    className="absolute left-2.5 top-1/2 -translate-y-1/2 z-20 w-8 h-8 rounded-full bg-slate-900/60 backdrop-blur-md text-white flex items-center justify-center active:scale-90 transition-transform shadow-md border border-white/20"
                                >
                                    <ChevronLeft className="w-4 h-4" />
                                </button>
                                <button
                                    onClick={isRTL ? prevFeature : nextFeature}
                                    aria-label="Next"
                                    className="absolute right-2.5 top-1/2 -translate-y-1/2 z-20 w-8 h-8 rounded-full bg-slate-900/60 backdrop-blur-md text-white flex items-center justify-center active:scale-90 transition-transform shadow-md border border-white/20"
                                >
                                    <ChevronRight className="w-4 h-4" />
                                </button>

                                {/* Dot Indicators */}
                                <div className="absolute bottom-3.5 inset-x-0 flex justify-center items-center gap-1.5 z-20 pointer-events-none">
                                    {features.map((_, i) => (
                                        <div
                                            key={i}
                                            className="h-1.5 rounded-full transition-all duration-300"
                                            style={{
                                                width: i === activeFeature ? "18px" : "5px",
                                                backgroundColor: i === activeFeature ? "white" : "rgba(255,255,255,0.45)",
                                            }}
                                        />
                                    ))}
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Integrated Mobile Feature Info Card */}
                    <AnimatePresence mode="wait">
                        <motion.div
                            key={activeFeature}
                            initial={{ opacity: 0, y: 12 }}
                            animate={{ opacity: 1, y: 0 }}
                            exit={{ opacity: 0, y: -12 }}
                            transition={{ duration: 0.22 }}
                            className="bg-white p-5 rounded-2xl border border-slate-200/90 shadow-lg relative overflow-hidden"
                            style={{
                                borderTop: `3px solid ${currentFeature.color}`,
                            }}
                        >
                            <div className="flex items-start gap-3.5">
                                <div
                                    className="w-11 h-11 rounded-xl flex items-center justify-center text-white shrink-0 shadow-sm"
                                    style={{ background: currentFeature.gradient }}
                                >
                                    {currentFeature.icon}
                                </div>
                                <div className="flex-1 min-w-0">
                                    <div className="flex items-center justify-between gap-2 mb-1">
                                        <h3 className="font-bold text-base text-slate-900 truncate">
                                            {currentFeature.title}
                                        </h3>
                                    </div>
                                    {currentFeature.subtitle && (
                                        <p className="text-xs font-medium mb-1.5" style={{ color: currentFeature.color }}>
                                            {currentFeature.subtitle}
                                        </p>
                                    )}
                                    <p className="text-xs text-slate-600 leading-relaxed">
                                        {currentFeature.description}
                                    </p>
                                </div>
                            </div>
                        </motion.div>
                    </AnimatePresence>
                </div>

                {/* ══════════════════════════════════════════════════════════════
                    DESKTOP VIEW (Visible on lg+ screens)
                    ══════════════════════════════════════════════════════════════ */}
                <div className="hidden lg:flex flex-row-reverse items-center gap-12 xl:gap-16">
                    {/* Desktop Phone Mockup Showcase */}
                    <motion.div
                        initial={{ opacity: 0, x: 30 }}
                        whileInView={{ opacity: 1, x: 0 }}
                        transition={{ duration: 0.5, delay: 0.15 }}
                        viewport={{ once: true }}
                        className="w-5/12 flex justify-center"
                    >
                        {desktopPhoneScreenshot}
                    </motion.div>

                    {/* Desktop Feature Card List */}
                    <div className="w-7/12 space-y-3.5">
                        {features.map((feature, index) => {
                            const isSelected = activeFeature === index;
                            return (
                                <motion.div
                                    key={index}
                                    initial={{ opacity: 0, x: -25 }}
                                    whileInView={{ opacity: 1, x: 0 }}
                                    transition={{ duration: 0.4, delay: 0.1 + index * 0.08 }}
                                    viewport={{ once: true }}
                                    onClick={() => setActiveFeature(index)}
                                    whileHover={{ scale: 1.015, x: isRTL ? -4 : 4 }}
                                    className="p-5 rounded-[20px] cursor-pointer transition-all duration-300 relative overflow-hidden group"
                                    style={{
                                        backgroundColor: isSelected ? feature.bgLight : "#FFFFFF",
                                        border: isSelected
                                            ? `1.5px solid ${feature.border}`
                                            : "1px solid rgba(226, 232, 240, 0.9)",
                                        boxShadow: isSelected
                                            ? `0 12px 28px -8px ${feature.glow}, 0 4px 12px rgba(0,0,0,0.02)`
                                            : "0 2px 8px rgba(0, 0, 0, 0.03)",
                                    }}
                                >
                                    <div className="flex items-start gap-4 relative z-10">
                                        {/* Optimized Custom Icon Container */}
                                        <div
                                            className="w-12 h-12 rounded-2xl flex items-center justify-center flex-shrink-0 transition-all duration-300"
                                            style={{
                                                background: isSelected ? feature.gradient : "rgba(241, 245, 249, 1)",
                                                color: isSelected ? "#FFFFFF" : feature.color,
                                                boxShadow: isSelected
                                                    ? `0 6px 16px ${feature.glow}`
                                                    : "none",
                                            }}
                                        >
                                            {feature.icon}
                                        </div>

                                        <div className="flex-1 text-start">
                                            <div className="flex items-center justify-between gap-2 mb-0.5">
                                                <h3
                                                    className="font-bold text-base transition-colors duration-200"
                                                    style={{
                                                        color: isSelected ? feature.color : "#0F172A",
                                                    }}
                                                >
                                                    {feature.title}
                                                </h3>
                                                {feature.subtitle && (
                                                    <span
                                                        className="text-[11px] font-semibold px-2.5 py-0.5 rounded-full"
                                                        style={{
                                                            backgroundColor: isSelected ? `${feature.color}15` : "rgba(241, 245, 249, 1)",
                                                            color: isSelected ? feature.color : "#64748B",
                                                        }}
                                                    >
                                                        {feature.subtitle}
                                                    </span>
                                                )}
                                            </div>

                                            <p
                                                className="text-xs leading-relaxed transition-colors duration-200"
                                                style={{ color: isSelected ? "#334155" : "#64748B" }}
                                            >
                                                {feature.description}
                                            </p>
                                        </div>
                                    </div>

                                    {/* Active Left/Right Accent Bar */}
                                    {isSelected && (
                                        <motion.div
                                            layoutId="activeAccentBar"
                                            className={`absolute top-0 bottom-0 ${isRTL ? "right-0" : "left-0"} w-1`}
                                            style={{ background: feature.gradient }}
                                            transition={{ type: "spring", stiffness: 400, damping: 30 }}
                                        />
                                    )}
                                </motion.div>
                            );
                        })}
                    </div>
                </div>
            </div>
        </section>
    );
};

export default AppExperience;
